import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const getMarkerColor = (status) => {
  switch (status) {
    case 'InService': return '#10b981'; // green
    case 'Faulty': return '#ef4444'; // red
    case 'UnderRepair': return '#eab308'; // yellow
    case 'Overload': return '#f59e0b'; // orange
    case 'OutOfOrder': return '#6b7280'; // gray
    default: return '#3b82f6'; // blue
  }
};

const createCustomIcon = (status, type) => {
  const color = getMarkerColor(status);
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; display: flex; align-items: center; justify-center; font-weight: bold; color: white; font-size: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">${type.substring(0,3)}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
};

const RecenterMap = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, map.getZoom());
  }, [center, map]);
  return null;
};

// Create village marker icon (blue thick dot)
const createVillageIcon = () => {
  return L.divIcon({
    className: 'village-marker',
    html: `<div style="background-color: #3b82f6; width: 12px; height: 12px; border-radius: 50%; border: 3px solid #1e40af; box-shadow: 0 2px 6px rgba(59, 130, 246, 0.5);"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
};

// Get boundary styling based on area type
const getBoundaryStyle = (areaType) => {
  switch (areaType) {
    case 'District':
      return {
        color: '#ef4444', // Red
        weight: 4, // Thick line
        fillOpacity: 0.05,
        fillColor: '#ef4444'
      };
    case 'Taluka':
      return {
        color: '#3b82f6', // Blue
        weight: 2, // Thinner line
        fillOpacity: 0.03,
        fillColor: '#3b82f6'
      };
    case 'Village':
    case 'Panchayat':
      return {
        color: '#10b981', // Green
        weight: 1.5, // Thin line
        fillOpacity: 0.02,
        fillColor: '#10b981'
      };
    default:
      return {
        color: '#6b7280',
        weight: 1,
        fillOpacity: 0.02,
        fillColor: '#6b7280'
      };
  }
};

const LeafletMap = ({ assets, areas = [], nrwData = [], selectedLayers, onAssetClick, center }) => {
  const shouldShowAsset = (asset) => {
    if (asset.type === 'Reservoir' && !selectedLayers.reservoirs) return false;
    if (asset.type === 'WTP' && !selectedLayers.wtp) return false;
    if (asset.type === 'OHT' && !selectedLayers.oht) return false;
    if (asset.type === 'Pipeline' && !selectedLayers.pipelines) return false;
    if (asset.type === 'DMA' && !selectedLayers.dma) return false;
    if (asset.type === 'PumpHouse' && !selectedLayers.pumphouse) return false;
    return true;
  };

  const filteredAssets = assets.filter(shouldShowAsset);
  
  // Only render actual boundaries from database - no mock generation
  const districts = areas.filter(a => a.type === 'District' && a.boundary);
  const talukas = areas.filter(a => a.type === 'Taluka' && a.boundary);
  const allVillages = areas.filter(a => (a.type === 'Village' || a.type === 'Panchayat'));
  const villagesWithBoundary = allVillages.filter(v => v.boundary);
  const villagePoints = allVillages.filter(v => v.latitude && v.longitude && !v.boundary);
  
  const mapCenter = center || [15.2832, 73.9882]; // Default to Margao, South Goa

  return (
    <MapContainer
      center={mapCenter}
      zoom={12}
      style={{ height: '100%', width: '100%' }}
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      
      <RecenterMap center={mapCenter} />
      
      {/* Boundary lines removed as per user request */}
      
      {/* Village Points - Blue Thick Dot */}
      {villagePoints.map((village) => (
        <Circle
          key={`village-point-${village.id}`}
          center={[village.latitude, village.longitude]}
          radius={200} // 200 meters radius
          pathOptions={{
            color: '#1e40af', // Dark blue border
            weight: 3, // Thick border
            fillColor: '#3b82f6', // Blue fill
            fillOpacity: 0.6
          }}
        >
          <Popup>
            <div>
              <h4 style={{ margin: '0 0 5px 0', color: '#3b82f6', fontWeight: 'bold' }}>
                {village.name}
              </h4>
              <p style={{ margin: 0, fontSize: '12px' }}>Village</p>
              {village.population && <p style={{ margin: '5px 0 0 0', fontSize: '11px' }}>Population: {village.population.toLocaleString()}</p>}
              {village.households_total && <p style={{ margin: '2px 0 0 0', fontSize: '11px' }}>Households: {village.households_total.toLocaleString()}</p>}
              <p style={{ margin: '2px 0 0 0', fontSize: '10px', color: '#6b7280' }}>
                {village.latitude.toFixed(4)}, {village.longitude.toFixed(4)}
              </p>
            </div>
          </Popup>
        </Circle>
      ))}
      
      {/* Water Infrastructure Assets */}
      {filteredAssets.map((asset) => (
        <Marker
          key={asset.id}
          position={[asset.latitude, asset.longitude]}
          icon={createCustomIcon(asset.status, asset.type)}
          eventHandlers={{
            click: () => onAssetClick(asset),
          }}
        >
          <Popup>
            <div style={{ minWidth: '220px' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#00d9ff', fontSize: '14px', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                {asset.name}
              </h3>
              <div style={{ fontSize: '12px', color: '#e5e7eb' }}>
                <p style={{ marginBottom: '4px' }}><strong style={{ color: '#9ca3af' }}>Type:</strong> {asset.type}</p>
                <p style={{ marginBottom: '4px' }}><strong style={{ color: '#9ca3af' }}>Status:</strong> <span style={{ color: getMarkerColor(asset.status), fontWeight: 'bold' }}>{asset.status}</span></p>
                {asset.capacity && <p style={{ marginBottom: '4px' }}><strong style={{ color: '#9ca3af' }}>Capacity:</strong> {asset.capacity} L</p>}
                <p style={{ marginBottom: '4px' }}><strong style={{ color: '#9ca3af' }}>Location:</strong> {asset.latitude.toFixed(4)}, {asset.longitude.toFixed(4)}</p>
                <p style={{ marginBottom: '8px' }}><strong style={{ color: '#9ca3af' }}>Last Update:</strong> {new Date(asset.last_update).toLocaleDateString()}</p>
                
                {(() => {
                  const assetNRW = nrwData.find(n => n.asset_id === asset.id);
                  if (assetNRW) {
                    const nrwColor = assetNRW.nrw_percentage > 25 ? '#ef4444' : assetNRW.nrw_percentage > 20 ? '#f59e0b' : assetNRW.nrw_percentage > 15 ? '#eab308' : '#10b981';
                    return (
                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #374151' }}>
                        <p style={{ margin: '0 0 6px 0', color: '#00d9ff', fontWeight: 'bold', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>NRW Data</p>
                        <p style={{ marginBottom: '3px', fontSize: '11px' }}><strong style={{ color: '#9ca3af' }}>NRW:</strong> <span style={{ color: nrwColor, fontWeight: 'bold' }}>{assetNRW.nrw_percentage.toFixed(2)}%</span></p>
                        <p style={{ marginBottom: '3px', fontSize: '11px' }}><strong style={{ color: '#9ca3af' }}>Water Supplied:</strong> {assetNRW.water_supplied.toFixed(0)} L</p>
                        <p style={{ marginBottom: '3px', fontSize: '11px' }}><strong style={{ color: '#9ca3af' }}>Water Billed:</strong> {assetNRW.water_billed.toFixed(0)} L</p>
                        <p style={{ marginBottom: '3px', fontSize: '11px' }}><strong style={{ color: '#9ca3af' }}>Loss:</strong> <span style={{ color: '#ef4444' }}>{assetNRW.estimated_loss.toFixed(0)} L</span></p>
                        <p style={{ marginBottom: '0', fontSize: '11px' }}><strong style={{ color: '#9ca3af' }}>Leakage Points:</strong> <span style={{ color: '#f59e0b' }}>{assetNRW.leakage_points}</span></p>
                      </div>
                    );
                  } else {
                    return (
                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #374151' }}>
                        <p style={{ margin: '0', color: '#6b7280', fontSize: '11px', fontStyle: 'italic' }}>No NRW data available</p>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

export default LeafletMap;
